import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import { useGatewaysContextProvider } from 'features/gateway/context/gateway-provider';
import { GatewayType } from 'features/gateway/domain/models/gateway-type';
import GatewaySettingsEspaClient from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings-clients/gateway-settings-espa-client';
import GatewaySettingsIoTEdgeClient from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings-clients/gateway-settings-iot-edge-client';
import GatewaySettingsIoTEdgeClusterClient from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings-clients/gateway-settings-iot-edge-cluster-client';
import GatewaySettingsViewpointClient from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings-clients/gateway-settings-viewpoint-client';
import GatewaySettingsConfiguration from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings-configuration';
import GatewaySettingsKadex from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings-kadex';
import GatewaySettingsMediaserver from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings-mediaserver';
import GatewaySettingsOfflinePortalCredentials from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings-offline-portal-credentials';
import GatewaySettingsOrganisation from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings-organisation';
import GatewaySettingsSonevoApi from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings-sonevo-api';
import styles from 'features/gateway/views/gateway-details/gateway-settings/gateway-settings.module.scss';

const GatewaySettings = (): React.JSX.Element => {
  const { t } = useTranslation('gateway');
  const { hasPermission } = usePermissionAuthorization();

  const { gatewayDetailsHook } = useGatewaysContextProvider();

  const { watch } = useFormContext();
  const watchGatewayType = watch('type');
  const watchIsAlarmmonitorEnabled = watch('isAlarmmonitorEnabled');
  const watchIsKadexApiEnabled = watch('isKadexApiEnabled');
  const watchIsSonevoApiEnabled = watch('isSonevoApiEnabled');
  const watchIsMediaserverEnabled = watch('isMediaserverEnabled');

  const isViewing = gatewayDetailsHook.viewingMode === 'viewing';
  const isCreating = gatewayDetailsHook.viewingMode === 'creation';
  const selectedGateway = gatewayDetailsHook.currentSelectedGateway;

  const hasSelectedType = GatewayType.Unknown !== watchGatewayType;
  const isViewpoint = selectedGateway?.type === GatewayType.Viewpoint;
  const isIoTEdge = selectedGateway?.type === GatewayType.IoTEdge;
  const isIoTEdgeCluster = watchGatewayType === GatewayType.IoTEdgeCluster;
  const isIoTEdgeMediaServerGatewayCluster = watchGatewayType === GatewayType.IoTEdgeMediaServerGatewayCluster;
  const showIoTEdgeFields = watchGatewayType === GatewayType.IoTEdge || watchGatewayType === GatewayType.IoTEdgeCluster;
  const isESPA = watchGatewayType === GatewayType.ESPA;
  const showGatewaySettings =
    ((isCreating && (isIoTEdgeCluster || isIoTEdgeMediaServerGatewayCluster)) || !isCreating) &&
    watchGatewayType !== GatewayType.NFC &&
    watchGatewayType !== GatewayType.BLEBeacon;

  if (!hasSelectedType) return <></>;

  return (
    <div className={`${styles.container} ${isViewing && styles.viewing}`}>
      <div className={styles.settingsItem} data-testid="gatewayOrganisationContainer">
        <Typography variant="h5">{t('details.settings.organisationUnitLabel')}</Typography>
        <div className={styles.settingsItemContent}>
          <GatewaySettingsOrganisation />
        </div>
      </div>

      {showGatewaySettings && hasPermission(Permission.CreateGateways) && (
        <>
          <Divider />
          <div className={styles.settingsItem}>
            <Typography variant="h5" className={styles.settingsTitle}>
              {t('details.settings.clientSettingsLabel')}
            </Typography>
            {isViewpoint && <GatewaySettingsViewpointClient />}
            {isIoTEdge && <GatewaySettingsIoTEdgeClient />}
            {(isIoTEdgeCluster || isIoTEdgeMediaServerGatewayCluster) && <GatewaySettingsIoTEdgeClusterClient />}
            {isESPA && <GatewaySettingsEspaClient />}
          </div>
        </>
      )}

      {showIoTEdgeFields && (
        <>
          <Divider />
          <div className={styles.settingsItem}>
            <Typography variant="h5">{t('details.settings.configurationLabel')}</Typography>
            <GatewaySettingsConfiguration />
          </div>
          <Divider />
          {watchIsSonevoApiEnabled && (
            <>
              <div className={styles.settingsItem}>
                <Typography variant="h5" className={styles.settingsTitle}>
                  {t('details.settings.sonevoApiLabel')}
                </Typography>
                <GatewaySettingsSonevoApi />
              </div>
              <Divider />
            </>
          )}
          {watchIsAlarmmonitorEnabled && (
            <>
              <div className={styles.settingsItem}>
                <Typography variant="h5" className={styles.settingsTitle}>
                  {t('details.settings.offlinePortalCredentialsLabel')}
                </Typography>
                <GatewaySettingsOfflinePortalCredentials />
              </div>
              <Divider />
            </>
          )}
          {watchIsKadexApiEnabled && (
            <>
              <div className={styles.settingsItem}>
                <Typography variant="h5" className={styles.settingsTitle}>
                  {t('details.settings.kadexLabel')}
                </Typography>
                <GatewaySettingsKadex />
              </div>
              <Divider />
            </>
          )}
        </>
      )}
      {isIoTEdgeMediaServerGatewayCluster && <Divider />}
      {(watchIsMediaserverEnabled || isIoTEdgeMediaServerGatewayCluster) && (
        <>
          <div className={styles.settingsItem}>
            <Typography variant="h5" className={styles.settingsTitle}>
              {t('details.settings.mediaserverLabel')}
            </Typography>
            <GatewaySettingsMediaserver />
          </div>
          <Divider />
        </>
      )}
    </div>
  );
};

export default GatewaySettings;
